import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ClaimsResponse } from '@dav-model/api/response/claims.response';
import { ClientConfigResponse } from '@dav-model/api/response/client-config.response.model';
import { DecodeVinResponse } from '@dav-model/api/response/decode-vin.response';
import { ManualResponse } from '@dav-model/api/response/manual.response';
import {
  ContractHistoryResponse,
  PendingContractResponse,
  RemittedContractResponse,
} from '@dav-model/api/response/pending-contract.response.model';
import { RecentQuotes } from '@dav-model/api/response/recent-quotes.response.model';
import { ResourcesLinkResponse } from '@dav-model/api/response/resources-link.response.model';
import { SourceApiType } from '@dav-model/api/source-api.type';
import { Cart } from '@dav-model/cart/cart.model';
import { ValidatedCart } from '@dav-model/cart/validate-cart.model';
import { LienholderResponse } from '@dav-model/checkout/submit_cart/response/lienholder.response';
import { SubmitCartResponse } from '@dav-model/checkout/submit_cart/response/submit-cart.response.model';
import { Forms } from '@dav-model/form/form';
import { ForgotOktaPasswordRequest } from '@dav-model/login/request/forgot-okta-password.request';
import { OktaLoginRequest } from '@dav-model/login/request/okta-login.request';
import { ResetOktaPasswordRequest } from '@dav-model/login/request/reset-okta-password.request';
import { Markup, UserResponse } from '@dav-model/login/response/user.response.model';
import { AggregatorRequest } from '@dav-model/request/aggregator.request';
import { GetProductsRequest } from '@dav-model/request/product.request';
import { ForgotPasswordFlow } from '@dav-model/response/forgot-password-flow.response';
import { GetProductsResponse } from '@dav-model/response/getproducts.response';
import { ApplicationCode } from 'environments/environment.model';
import { Observable, throwError as observableThrowError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ConfigService } from './config.service';

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly URL: string = '';
  private oktaHeaders = new HttpHeaders({ 'Cache-Control': 'no-cache', 'Content-Type': 'application/json' });

  constructor(private http: HttpClient, private configService: ConfigService) {
    this.URL = configService.getApiUrl();
  }

  login(request: any): Observable<UserResponse> {
    return this.http.post<UserResponse>(`${this.URL}/login`, request).pipe(catchError(this.errorHandler));
  }

  getOktaUser(oktaLoginRequest: OktaLoginRequest): Observable<UserResponse> {
    return this.http.post<UserResponse>(`${this.URL}/okta/getOktaUser`, oktaLoginRequest);
  }

  forgotOktaUserName(request: any): Observable<void> {
    return this.http.post<void>(`${this.URL}/okta/forgotusername`, request, { headers: this.oktaHeaders });
  }

  forgotOktaPassword(
    request: ForgotOktaPasswordRequest
  ): Observable<{ flowType: 'OKTA' | 'LEGACY'; success: boolean; status: boolean }> {
    return this.http
      .post<{ flowType: 'OKTA' | 'LEGACY'; success: boolean; status: boolean }>(
        `${this.URL}/okta/forgotpassword`,
        request,
        { headers: this.oktaHeaders }
      )
      .pipe(catchError(this.errorHandler));
  }

  resetOktaPassword(request: ResetOktaPasswordRequest): Observable<void> {
    return this.http.post<void>(`${this.URL}/okta/resetpassword`, request, { headers: this.oktaHeaders });
  }

  forgotPasswordOktaFlow(identifier: string): Observable<ForgotPasswordFlow> {
    return this.http.post<any>(`${this.URL}/okta/forgotpassword`, { identifier }).pipe(catchError(this.errorHandler));
  }

  ssoLogin(request: any) {
    return this.http.post<UserResponse>(`${this.URL}/ssologin`, request).pipe(catchError(this.errorHandler));
  }

  validatePasswordResetToken(token): Observable<{ stateToken: string; username: string }> {
    return this.http.get<{ stateToken: string; username: string }>(`${this.URL}/okta/resetpasswordvalid/${token}`);
  }

  getProducts(request: GetProductsRequest): Observable<GetProductsResponse> {
    return this.http.post<GetProductsResponse>(`${this.URL}/products`, request).pipe(catchError(this.errorHandler));
  }

  getProductsFromAggregator(request: AggregatorRequest): Observable<GetProductsResponse> {
    return this.http
      .post<GetProductsResponse>(`${this.URL}/aggregator/rates`, request)
      .pipe(catchError(this.errorHandler));
  }

  validateCart(request: Cart) {
    return this.http.post<ValidatedCart>(`${this.URL}/cartvalidation`, request).pipe(catchError(this.errorHandler));
  }

  submitCart(request: any) {
    return this.http.post<SubmitCartResponse>(`${this.URL}/savecontract`, request);
  }

  generatePDF(data: { contractNumber: string; vendor: string; sourceApi?: SourceApiType }) {
    const request = { ...data, pdfformat: 'BINARY', dealerNumber: this.configService.getCMSDealerNumber() };

    return this.http
      .post(`${this.URL}/generatePdf`, request, { responseType: 'blob' as 'json' })
      .pipe(map((res: any) => new Blob([res], { type: 'application/pdf' })));
  }

  getClientInfo(application: ApplicationCode) {
    return this.http
      .get<ClientConfigResponse>(`${this.URL}/getclientinfo`, { params: { application } })
      .pipe(catchError(this.errorHandler));
  }

  saveMarkup(markup: Markup, username: string) {
    return this.http.post<any>(`${this.URL}/userdefaultadd`, { username, markup }).pipe(catchError(this.errorHandler));
  }

  editContract(contract: any) {
    return this.http.post<any>(`${this.URL}/updatependingcontract`, contract).pipe(catchError(this.errorHandler));
  }

  updateuser(user: any) {
    return this.http.post<any>(`${this.URL}/updateuser`, user).pipe(catchError(this.errorHandler));
  }

  adduser(user: any) {
    return this.http.post<any>(`${this.URL}/adduser`, user).pipe(catchError(this.errorHandler));
  }

  getRecentQuotes(cmsDealerNumber: string): Observable<RecentQuotes[]> {
    return this.http
      .post<RecentQuotes[]>(`${this.URL}/getrecentquote`, { dealer: cmsDealerNumber })
      .pipe(catchError(this.errorHandler));
  }

  getPostal(request: any) {
    return this.http.post<any>(`${this.URL}/getcitystate`, request).pipe(catchError(this.errorHandler));
  }

  getReports(request: any) {
    return this.http.post<any>(`${this.URL}/getreports`, request).pipe(catchError(this.errorHandler));
  }

  getResourceMenu(request: any) {
    return this.http.post<any>(`${this.URL}/getresourcemenu`, request).pipe(catchError(this.errorHandler));
  }

  getResourceLink(request: {
    application: string;
    applicationId: string;
    relatedApplicationId: any;
    username: string;
    cmsDealerNumber: any;
    id: any;
    stateId: any;
  }) {
    return this.http
      .post<ResourcesLinkResponse>(`${this.URL}/getresourcelink`, request)
      .pipe(catchError(this.errorHandler));
  }

  getContractHistory(request: any) {
    return this.http
      .post<ContractHistoryResponse>(`${this.URL}/getcontracthistory`, request)
      .pipe(catchError(this.errorHandler));
  }

  generateResourcePDF(pdfId: string) {
    return this.http
      .post(`${this.URL}/getpdf`, { id: pdfId }, { responseType: 'blob' as 'json' })
      .pipe(map((res: any) => new Blob([res], { type: 'application/pdf' })));
  }

  getPendingContracts(request: any) {
    return this.http
      .post<PendingContractResponse>(`${this.URL}/getpendingcontracts`, request)
      .pipe(catchError(this.errorHandler));
  }

  getRemittedContracts(request: any) {
    return this.http
      .post<RemittedContractResponse>(`${this.URL}/getremittedcontracts`, request)
      .pipe(catchError(this.errorHandler));
  }

  submitPendingContracts(request: any) {
    return this.http
      .post<PendingContractResponse>(`${this.URL}/remitcontracts`, request)
      .pipe(catchError(this.errorHandler));
  }

  getLookerReportlink(request: any) {
    return this.http.post<any>(`${this.URL}/looker/embeded-link`, request).pipe(catchError(this.errorHandler));
  }

  decodeVin(request: any) {
    return this.http
      .get<DecodeVinResponse>(`${this.URL}/vindecode`, { params: request })
      .pipe(catchError(this.errorHandler));
  }

  undoDecline(request: any) {
    return this.http
      .post<PendingContractResponse>(`${this.URL}/undodecline`, request)
      .pipe(catchError(this.errorHandler));
  }

  verifyUsername(request: any) {
    return this.http.post<ClaimsResponse>(`${this.URL}/verifyusername`, request).pipe(catchError(this.errorHandler));
  }

  getClaims(request: any) {
    return this.http.post<ClaimsResponse>(`${this.URL}/getclaims`, request).pipe(catchError(this.errorHandler));
  }

  manualDecode(
    category: string = null,
    year: string = null,
    make: string = null,
    model: string = null
  ): Observable<ManualResponse> {
    const request = {
      programId: localStorage.getItem('programId'),
      vehicleType: category,
      year,
      make,
      model,
    };

    return this.http
      .get<ManualResponse>(`${this.URL}/manualdecode`, { params: request })
      .pipe(catchError(this.errorHandler));
  }

  refreshToken() {
    return this.http.get<any>(`${this.URL}/refreshtoken`).pipe(catchError(this.errorHandler));
  }

  saveQuote(quote: any) {
    return this.http.post<any>(`${this.URL}/saveQuote`, quote).pipe(catchError(this.errorHandler));
  }

  loadQuote(request) {
    return this.http.post<any>(`${this.URL}/getQuote`, request).pipe(catchError(this.errorHandler));
  }

  loadQuoteOnly(request) {
    return this.http.post<any>(`${this.URL}/getQuoteOnly`, request).pipe(catchError(this.errorHandler));
  }

  deleteQuote(request) {
    return this.http.post<any>(`${this.URL}/cartdelete`, request).pipe(catchError(this.errorHandler));
  }

  getProductForm(application: ApplicationCode): Observable<Forms> {
    return this.http.post<Forms>(`${this.URL}/getproductform`, { application }).pipe(catchError(this.errorHandler));
  }

  getLienholder(term: string): Observable<LienholderResponse[]> {
    return this.http
      .post<LienholderResponse[]>(`${this.URL}/getlienholder`, { lienholder: term })
      .pipe(catchError(this.errorHandler));
  }

  getrequestQuote(request: any): Observable<any> {
    return this.http.post<any>(`${this.URL}/umu-insurance/quote`, request).pipe(catchError(this.errorHandler));
  }

  getUMUCredit(request: any) {
    return this.http.post<any>(`${this.URL}/umu-insurance/contract`, request).pipe(catchError(this.errorHandler));
  }

  addLienholder(request: any): Observable<any> {
    return this.http.post<any>(`${this.URL}/addlienholder`, request).pipe(catchError(this.errorHandler));
  }

  forgotUsername(request: any): Observable<any> {
    return this.http.post<any>(`${this.URL}/forgotusername`, request).pipe(catchError(this.errorHandler));
  }

  forgotPassword(request: any): Observable<any> {
    return this.http.post<any>(`${this.URL}/forgotpassword`, request).pipe(catchError(this.errorHandler));
  }

  getDealerInfo(request: any): Observable<any> {
    return this.http.post<any>(`${this.URL}/getdealerinfo`, request).pipe(catchError(this.errorHandler));
  }

  getDealer(request: any): Observable<any> {
    return this.http.post<any>(`${this.URL}/getdealer`, request).pipe(catchError(this.errorHandler));
  }

  getassociateDealers(request: any): Observable<any> {
    return this.http.post<any>(`${this.URL}/getalldealers`, request).pipe(catchError(this.errorHandler));
  }

  getCheckoutForm(request: Cart): Observable<Forms> {
    return this.http.post<Forms>(`${this.URL}/getcheckoutform`, request).pipe(catchError(this.errorHandler));
  }

  requestAccount(request: any): Observable<any> {
    return this.http.post<any>(`${this.URL}/requestaccount`, request).pipe(catchError(this.errorHandler));
  }

  updatePassword(request: any): Observable<any> {
    return this.http.post<any>(`${this.URL}/updatepassword`, request).pipe(catchError(this.errorHandler));
  }

  taxCalculation(request: any): Observable<any> {
    return this.http.post<any>(`${this.URL}/taxcalculation`, request).pipe(catchError(this.errorHandler));
  }

  getTranslation(request: any) {
    return this.http.get<any>(this.getLanguagePath(request)).pipe(
      catchError((err) => {
        console.warn(err);

        return this.http.get<any>('../../../assets/i18n/en-US.json');
      })
    );
  }

  errorHandler(error: HttpErrorResponse) {
    return observableThrowError(error);
  }

  //////////////////////////////////////////////////////////////////////////////////
  //  Language defaults ////////////////////////////////////////////////////////////
  getLanguagePath(request: any) {
    const language = request.language;
    switch (language) {
      case 'en-US':
      default: {
        return '../../../assets/i18n/en-US.json';
      }
      case 'fr-CA': {
        return '../../../assets/i18n/fr-CA.json';
      }
    }
  }
}
